import React from "react";
import { Helmet } from "react-helmet-async";
import PropTypes from "prop-types";

const SEO = ({
  title,
  description,
  image,
  type = "website",
  url,
  book = null,
}) => {
  const siteName = "Hasa Book Store";
  const defaultDescription =
    "Discover a world of knowledge with Hasa Book Store - Your trusted source for educational books and resources.";
  const siteUrl = "https://hasabookstore.com";
  const defaultImage = `${siteUrl}/logo.png`;

  // Format title
  const formattedTitle = title ? `${title} | ${siteName}` : siteName;

  // Use provided values or fallbacks
  const metaDescription = description || defaultDescription;

  // Ensure the image URL is absolute
  let metaImage = image || defaultImage;
  // If the image starts with a slash but doesn't have the domain, add the domain
  if (metaImage && metaImage.startsWith("/")) {
    metaImage = `${siteUrl}${metaImage}`;
  }
  // If the image doesn't have http(s), assume it needs the full domain
  else if (metaImage && !metaImage.startsWith("http")) {
    metaImage = `${siteUrl}/${metaImage}`;
  }

  const canonicalUrl =
    url || typeof window !== "undefined" ? window.location.href : "";

  // Construct a more detailed description for books
  let enhancedDescription = metaDescription;
  if (book && book.added_by_display) {
    enhancedDescription = `${metaDescription} Written by ${book.added_by_display.join(", ")}.`;
    if (book.price) {
      enhancedDescription += ` Available for $${book.price}.`;
    }
  }

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{formattedTitle}</title>
      <meta name="description" content={enhancedDescription} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph Meta Tags */}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={formattedTitle} />
      <meta property="og:description" content={enhancedDescription} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={type} />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={formattedTitle} />
      <meta name="twitter:description" content={enhancedDescription} />
      <meta name="twitter:image" content={metaImage} />

      {/* Book-specific metadata */}
      {book && (
        <>
          <meta
            property="book:author"
            content={book.added_by_display?.join(", ")}
          />
          <meta property="book:isbn" content={book.isbn || ""} />
          <meta property="book:release_date" content={book.publication_date} />
          <meta property="og:price:amount" content={book.price} />
          <meta property="og:price:currency" content="USD" />
          <meta
            property="og:availability"
            content={book.inventory_status || "instock"}
          />

          {/* Additional book-specific meta tags for better SEO */}
          <meta property="product:price:amount" content={book.price} />
          <meta property="product:price:currency" content="USD" />
          <meta
            name="keywords"
            content={`book, ${book.title}, ${book.added_by_display?.join(", ")}, education, ${book.format}`}
          />
          {book.categories && book.categories.length > 0 && (
            <meta name="article:section" content={book.categories[0]} />
          )}
        </>
      )}
    </Helmet>
  );
};

SEO.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
  type: PropTypes.string,
  url: PropTypes.string,
  book: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    cover_image: PropTypes.string,
    added_by_display: PropTypes.arrayOf(PropTypes.string),
    isbn: PropTypes.string,
    publication_date: PropTypes.string,
    price: PropTypes.number,
  }),
};

export default React.memo(SEO);
